import { createContext, useContext, type ReactNode } from 'react';
import type { ApiClient } from '../api/client';

/**
 * What the hub shell knows that a section might need, so a section pulls it via `useHubContext()`
 * instead of receiving it through the registry's props — the registry only ever carries `element`.
 */
export interface HubContextValue {
  api: ApiClient;
  userLabel: string;

  /** Absent in local development mode, where there is no session to sign out of. */
  onSignOut?: () => void;

  /** Null when there is no mailbox to create a draft in — see LocalApp.tsx. */
  draftUnavailableReason: string | null;

  onConsentRequired: (scopes: string[]) => void;
}

const HubContext = createContext<HubContextValue | null>(null);

export function HubProvider({ value, children }: { value: HubContextValue; children: ReactNode }) {
  return <HubContext.Provider value={value}>{children}</HubContext.Provider>;
}

export function useHubContext(): HubContextValue {
  const context = useContext(HubContext);

  if (!context) {
    throw new Error('useHubContext() was called outside a HubProvider.');
  }

  return context;
}
